import { type BalanceSummary, percentOf } from '@familywallet/shared';
import { useTranslation } from 'react-i18next';
import { formatAmount } from '../lib/money';
import { Card } from './ui/card';

/** This month in the base currency (docs/DESIGN.md §4): income, expenses, and a spending bar. */
export function MonthCard({ summary }: { summary: BalanceSummary }) {
  const { t, i18n } = useTranslation();
  const { month } = summary;
  // Text can pass 100% (spent more than earned); the bar stops at full.
  const percent = percentOf(month.expense, month.income, 999);
  const shown = percentOf(month.expense, month.income, 100) ?? '0';
  const monthName = new Intl.DateTimeFormat(i18n.language, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${month.from}T00:00:00Z`));
  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-[15px] font-semibold">{monthName}</h2>
        {percent !== null && (
          <span className="text-[13px] text-muted">{t('home.spentOfIncome', { percent })}</span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col">
          <span className="text-[13px] text-muted">{t('home.income')}</span>
          <span className="text-lg font-semibold text-accent tabular-nums" dir="ltr">
            {formatAmount(month.income, summary.currency, i18n.language)}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-[13px] text-muted">{t('home.expenses')}</span>
          <span className="text-lg font-semibold text-expense tabular-nums" dir="ltr">
            {formatAmount(month.expense, summary.currency, i18n.language)}
          </span>
        </div>
      </div>
      <div
        className="h-2 overflow-hidden rounded-full bg-line"
        role="progressbar"
        aria-label={t('home.spentBar')}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Number.parseInt(shown, 10)}
      >
        <div className="h-full rounded-full bg-expense" style={{ width: `${shown}%` }} />
      </div>
    </Card>
  );
}
